import type { IntakeFormPreview } from "@/types/clinician";

export interface IntakeOutlier {
  code: "height" | "weight" | "bmi" | "goal_weight";
  label: string;
  detail: string;
}

interface NumericResponse {
  question: string;
  answer: string;
  value: number;
}

function numericAnswer(
  forms: IntakeFormPreview[],
  predicate: (question: string) => boolean,
): NumericResponse | null {
  for (const form of forms) {
    for (const answer of form.answers) {
      const question = answer.question.toLowerCase();
      if (!predicate(question)) continue;
      const match = answer.answer.replaceAll(",", "").match(/-?\d+(?:\.\d+)?/);
      if (match)
        return {
          question,
          answer: answer.answer.toLowerCase(),
          value: Number(match[0]),
        };
    }
  }
  return null;
}

function heightInches(response: NumericResponse | null) {
  if (!response) return null;
  const feetAndInches = response.answer.match(
    /(\d+(?:\.\d+)?)\s*(?:'|ft|feet|foot)\s*(\d+(?:\.\d+)?)?/,
  );
  if (feetAndInches) {
    return Number(feetAndInches[1]) * 12 + Number(feetAndInches[2] ?? 0);
  }
  if (response.question.includes("cm")) return response.value / 2.54;
  if (
    !response.question.includes("inch") &&
    /\b(?:ft|feet|foot)\b/.test(response.question)
  ) {
    return response.value * 12;
  }
  return response.value;
}

function weightPounds(response: NumericResponse | null) {
  if (!response) return null;
  return response.question.includes("kg")
    ? response.value / 0.45359237
    : response.value;
}

const isGoalWeight = (question: string) =>
  question.includes("goal") || question.includes("target");

function intakeHeight(forms: IntakeFormPreview[]) {
  return heightInches(
    numericAnswer(
      forms,
      (question) => question.includes("height") && !question.includes("weight"),
    ),
  );
}

/** Follow-up check-ins ask for the starting weight before the current one;
 * prefer an explicit current weight and never use the starting weight. Each
 * submission is read in full before the next, so values never mix. */
function currentWeight(forms: IntakeFormPreview[]) {
  for (const form of forms) {
    const response =
      numericAnswer(
        [form],
        (question) =>
          question.includes("weight") &&
          question.includes("current") &&
          !isGoalWeight(question),
      ) ??
      numericAnswer(
        [form],
        (question) =>
          question.includes("weight") &&
          !isGoalWeight(question) &&
          !/\bstart(?:ing)?\b/.test(question),
      );
    if (response) return weightPounds(response);
  }
  return null;
}

export type BmiFlag = "below_25" | "25_to_27" | null;

export interface BmiReading {
  value: number;
  flag: BmiFlag;
  heightIn: number;
  weightLb: number;
  /** Set when height came from another submission (follow-ups don't ask it). */
  heightForm: IntakeFormPreview | null;
}

/** GLP-1 review aid (Dr. Hinds, 2026-10-01): BMI below 25 is flagged red and
 * 25 to under 27 yellow. Not an eligibility decision. */
export function intakeBmi(
  form: IntakeFormPreview,
  allForms: IntakeFormPreview[],
): BmiReading | null {
  const weightLb = currentWeight([form]);
  if (weightLb == null || weightLb <= 0) return null;
  let heightIn = intakeHeight([form]);
  let heightForm: IntakeFormPreview | null = null;
  if (heightIn == null) {
    heightForm =
      allForms
        .filter((other) => other.id !== form.id && intakeHeight([other]) != null)
        .sort(
          (left, right) =>
            (Date.parse(right.submitted_at ?? "") || 0) -
            (Date.parse(left.submitted_at ?? "") || 0),
        )[0] ?? null;
    heightIn = heightForm ? intakeHeight([heightForm]) : null;
  }
  // Implausible heights are reported by the outlier checks instead.
  if (heightIn == null || heightIn < 48 || heightIn > 84) return null;
  const value =
    Math.round(((weightLb * 0.45359237) / (heightIn * 0.0254) ** 2) * 10) / 10;
  return {
    value,
    flag: value < 25 ? "below_25" : value < 27 ? "25_to_27" : null,
    heightIn,
    weightLb,
    heightForm,
  };
}

/** Review flags, not eligibility decisions. BMI below 20 requires review. */
export function findIntakeOutliers(
  forms: IntakeFormPreview[],
): IntakeOutlier[] {
  const height = intakeHeight(forms);
  const weight = currentWeight(forms);

  const goal = weightPounds(
    numericAnswer(
      forms,
      (question) => question.includes("weight") && isGoalWeight(question),
    ),
  );

  const outliers: IntakeOutlier[] = [];
  if (height != null && (height < 48 || height > 84)) {
    outliers.push({
      code: "height",
      label: "Extreme height value",
      detail: `${Math.round(height)} in is outside the 4–7 ft review range. Verify the intake units/value.`,
    });
  }
  if (weight != null && (weight < 70 || weight > 600)) {
    outliers.push({
      code: "weight",
      label: "Extreme current weight",
      detail: `${Math.round(weight)} lb is outside the 70–600 lb review range. Verify the intake units/value.`,
    });
  }
  if (height != null && weight != null && height >= 48 && height <= 84) {
    const bmi = (weight * 0.45359237) / (height * 0.0254) ** 2;
    if (weight > 0 && bmi < 20) {
      outliers.push({
        code: "bmi",
        label: "BMI below 20",
        detail: `The reported values imply BMI ${bmi.toFixed(1)}. Review the measurements and treatment suitability.`,
      });
    } else if (bmi > 70) {
      outliers.push({
        code: "bmi",
        label: "Height / weight mismatch",
        detail: `The reported values imply BMI ${bmi.toFixed(1)}. Verify height, weight, and units.`,
      });
    }
  }
  if (
    weight != null &&
    goal != null &&
    (goal < weight * 0.45 || goal > weight * 1.3)
  ) {
    outliers.push({
      code: "goal_weight",
      label: "Extreme goal-weight difference",
      detail: `Current ${Math.round(weight)} lb → goal ${Math.round(goal)} lb. Confirm this was entered correctly.`,
    });
  }
  return outliers;
}
